import React, { useContext, useState } from 'react';
import { AuthContext, API } from '@/App';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, Zap, Crown, Gem } from 'lucide-react';
import { showToast } from '@/lib/custom-toast';
import axios from 'axios';

const Pricing = () => {
  const { token, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly'); // monthly or annual

  const priceIds = {
    premiumMonthly: 'price_1TUYglJ42rbYbursKjX3zUVE', // $79
    premiumAnnual: 'price_1TUYgmJ42rbYbursfoGFcXfR',  // $600
    proMonthly: 'price_1TUYgmJ42rbYburs4pPjKFaf',    // $129
    proAnnual: 'price_1TUYgmJ42rbYbursvzzPg8xX'      // $996
  };

  // Estado de suscripción (solo 'trialing' cuenta como prueba; trial_ends_at histórico NO)
  const tier = user?.subscription_tier || 'free';
  const subStatus = user?.subscription_status || '';
  const isTrial = subStatus === 'trialing';
  const isActive = subStatus === 'active';
  const isPastDue = subStatus === 'past_due';
  const isProUser = tier === 'pro' && !isTrial;           // Pro en cualquier estado → solo ve Pro
  const isPaidPro = tier === 'pro' && isActive;
  const isPaidPremium = tier === 'premium' && isActive;
  const showPremium = !isProUser;

  const handleSubscribe = async (priceId, planName) => {
    if (!token) {
      showToast.error('Debes iniciar sesión para suscribirte');
      navigate('/');
      return;
    }

    setLoading(true);
    setSelectedPlan(planName);

    try {
      const response = await axios.post(
        `${API}/subscriptions/create-checkout-session`,
        { price_id: priceId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      showToast.error(error.response?.data?.detail || 'Error al crear sesión de pago');
    } finally {
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  const features = {
    free: [
      'Transacciones manuales ilimitadas',
      'Hasta 3 cuentas bancarias',
      'Categorías personalizadas',
      'Dashboard con estadísticas completas',
      'Transacciones recurrentes',
      'Registro de deudas y ligas SPEI',
      'Tarjetas de crédito y MSI'
    ],
    premium: [
      'Todo lo de Gratis',
      'Hasta 10 cuentas bancarias',
      '100 transacciones por voz al mes',
      'Chat IA Financiero con contexto real',
      'Objetivos financieros avanzados',
      'Sin publicidad ni interrupciones'
    ],
    pro: [
      'Todo lo de Premium',
      'Cuentas bancarias ilimitadas',
      '300 transacciones por voz al mes',
      'Exportaciones ilimitadas (Excel/PDF/CSV)',
      'Soporte prioritario VIP',
      'Acceso anticipado a nuevas funciones'
    ]
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-14">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-xs font-semibold uppercase tracking-widest text-stone-400 mb-3">Planes y Precios</p>
          <h1 className="text-3xl sm:text-5xl font-black text-stone-900 mb-4 tracking-tight">
            Simple y sin letra pequeña
          </h1>
          <p className="text-base sm:text-lg text-stone-600 max-w-xl mx-auto font-medium">
            Comienza gratis. Actualiza a Premium o Pro para automatizar con voz e inteligencia artificial.
          </p>

          {/* Billing Toggle */}
          <div className="mt-8 inline-flex items-center p-1 bg-stone-100 rounded-xl border border-stone-200 shadow-inner">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2.5 rounded-lg font-bold text-xs sm:text-sm active:scale-95 transition-all duration-200 ease-spring cursor-pointer ${
                billingCycle === 'monthly'
                  ? 'bg-white text-stone-900 shadow-sm'
                  : 'text-stone-500 hover:text-stone-800'
              }`}
            >
              Mensual
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-6 py-2.5 rounded-lg font-bold text-xs sm:text-sm active:scale-95 transition-all duration-200 ease-spring cursor-pointer flex items-center gap-1.5 ${
                billingCycle === 'annual'
                  ? 'bg-white text-stone-900 shadow-sm'
                  : 'text-stone-500 hover:text-stone-800'
              }`}
            >
              <span>Anual</span>
              <span className="bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-wider">
                Ahorra 37%
              </span>
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className={`grid grid-cols-1 gap-8 mx-auto items-stretch ${showPremium ? 'md:grid-cols-3 max-w-6xl' : 'md:grid-cols-2 max-w-4xl'}`}>
          {/* FREE PLAN */}
          <Card className="p-7 sm:p-8 bg-white border border-stone-200 rounded-3xl shadow-sm flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-bold uppercase tracking-wider text-stone-500">Plan Base</span>
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-stone-100 text-stone-600 border border-stone-200">
                  Para siempre
                </span>
              </div>
              <h2 className="text-2xl font-black text-stone-900 mb-2">Gratis</h2>
              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-4xl font-black text-stone-900">$0</span>
                <span className="text-stone-400 font-medium text-sm">/mes</span>
              </div>
              <p className="text-xs sm:text-sm text-stone-500 mb-6 font-medium">Todo lo esencial para llevar el control diario.</p>

              <ul className="space-y-3 mb-8 border-t border-stone-100 pt-6">
                {features.free.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2.5 text-xs sm:text-sm text-stone-600 font-medium">
                    <Check className="w-4 h-4 text-stone-400 shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            <Button 
              className="w-full bg-stone-100 hover:bg-stone-200 text-stone-800 font-bold py-5 rounded-xl cursor-default" 
              disabled={user?.subscription_tier === 'free'}
            >
              {user?.subscription_tier === 'free' ? 'Tu Plan Actual' : 'Plan Gratuito'}
            </Button>
          </Card>

          {/* PREMIUM PLAN (oculto para usuarios Pro) */}
          {showPremium && (() => {
            const isCurrentDisabled = isPaidPremium || (tier === 'premium' && isPastDue);

            return (
              <Card className="p-7 sm:p-8 bg-white border-2 border-indigo-600 rounded-3xl shadow-xl flex flex-col justify-between relative">
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-indigo-600 text-white px-3.5 py-1 text-[10px] font-black uppercase tracking-widest rounded-full shadow-md">
                  Más Popular
                </div>

                <div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs font-bold uppercase tracking-wider text-indigo-600">Recomendado</span>
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 border border-indigo-100">
                      14 días de prueba
                    </span>
                  </div>
                  <h2 className="text-2xl font-black text-stone-900 mb-2">Premium</h2>
                  <div className="flex items-baseline gap-1 mb-6">
                    <span className="text-4xl font-black text-stone-900">
                      {billingCycle === 'monthly' ? '$79' : '$50'}
                    </span>
                    <span className="text-stone-400 font-medium text-sm">/mes</span>
                    {billingCycle === 'annual' && (
                      <span className="text-xs text-stone-400 line-through ml-1">$79</span>
                    )}
                  </div>
                  <p className="text-xs sm:text-sm text-stone-500 mb-6 font-medium">Dictado por voz y asistente financiero IA inteligente.</p>

                  <ul className="space-y-3 mb-8 border-t border-stone-100 pt-6">
                    {features.premium.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2.5 text-xs sm:text-sm text-stone-700 font-medium">
                        <Check className="w-4 h-4 text-indigo-600 shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <Button
                  className={`w-full py-5 rounded-xl font-bold text-sm shadow-md active:scale-95 hover:scale-[1.01] transition-all duration-150 ease-spring cursor-pointer ${
                    isCurrentDisabled
                      ? 'bg-stone-100 text-stone-400 cursor-not-allowed shadow-none'
                      : 'bg-indigo-600 hover:bg-indigo-500 text-white'
                  }`}
                  onClick={() => handleSubscribe(
                    billingCycle === 'monthly' ? priceIds.premiumMonthly : priceIds.premiumAnnual,
                    `premium-${billingCycle}`
                  )}
                  disabled={(loading && selectedPlan === `premium-${billingCycle}`) || isCurrentDisabled}
                >
                  {isPaidPremium
                    ? 'Plan Actual'
                    : tier === 'premium' && isPastDue
                    ? 'Pago pendiente'
                    : isTrial && tier === 'premium'
                    ? 'Conservar Plan Premium'
                    : 'Empezar prueba de 14 días'}
                </Button>
              </Card>
            );
          })()}

          {/* PRO PLAN */}
          {(() => {
            const proLabel = isPaidPro
              ? 'Plan Actual'
              : isPastDue && (tier === 'pro' || tier === 'premium')
              ? 'Actualizar pago'
              : isProUser
              ? 'Renovar Pro'
              : isPaidPremium
              ? 'Subir a Pro'
              : isTrial && tier === 'pro'
              ? 'Conservar Plan Pro'
              : 'Empezar prueba de 14 días';

            return (
              <Card className="p-7 sm:p-8 bg-stone-900 border border-stone-800 text-white rounded-3xl shadow-xl flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs font-bold uppercase tracking-wider text-stone-400">Poder Total</span>
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-stone-800 text-stone-300 border border-stone-700">
                      14 días de prueba
                    </span>
                  </div>
                  <h2 className="text-2xl font-black text-white mb-2">Pro</h2>
                  <div className="flex items-baseline gap-1 mb-6">
                    <span className="text-4xl font-black text-white">
                      {billingCycle === 'monthly' ? '$129' : '$83'}
                    </span>
                    <span className="text-stone-400 font-medium text-sm">/mes</span>
                    {billingCycle === 'annual' && (
                      <span className="text-xs text-stone-500 line-through ml-1">$129</span>
                    )}
                  </div>
                  <p className="text-xs sm:text-sm text-stone-400 mb-6 font-medium">Capacidad ilimitada, reportes Excel/PDF y soporte prioritario.</p>

                  <ul className="space-y-3 mb-8 border-t border-stone-800 pt-6">
                    {features.pro.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2.5 text-xs sm:text-sm text-stone-300 font-medium">
                        <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <Button
                  className={`w-full py-5 rounded-xl font-bold text-sm shadow-md active:scale-95 hover:scale-[1.01] transition-all duration-150 ease-spring cursor-pointer ${
                    isPaidPro
                      ? 'bg-stone-800 text-stone-500 cursor-not-allowed shadow-none'
                      : 'bg-white hover:bg-stone-100 text-stone-900'
                  }`}
                  onClick={() => handleSubscribe(
                    billingCycle === 'monthly' ? priceIds.proMonthly : priceIds.proAnnual,
                    `pro-${billingCycle}`
                  )}
                  disabled={(loading && selectedPlan === `pro-${billingCycle}`) || isPaidPro}
                >
                  {proLabel}
                </Button>
                {isPaidPremium && (
                  <p className="mt-3 text-[11px] text-stone-400 font-medium text-center">
                    Si pagaste Premium hoy, se descuenta completo. Otros días se cobra solo la diferencia proporcional.
                  </p>
                )}
              </Card>
            );
          })()}
        </div>

        {/* Discount Codes Section */}
        {user?.subscription_tier === 'free' && (
          <div className="mt-14 max-w-4xl mx-auto">
            <Card className="p-6 bg-stone-50 border border-stone-200 rounded-3xl shadow-xs">
              <h3 className="text-sm font-bold text-stone-900 mb-3 text-center uppercase tracking-wider">
                Códigos Promocionales Disponibles
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="bg-white p-3 rounded-xl border border-stone-200 text-center shadow-xs">
                  <p className="font-mono font-bold text-stone-900 text-sm">LANZAMIENTO5</p>
                  <p className="text-[11px] text-stone-500 font-medium">5% en cualquier plan</p>
                </div>
                <div className="bg-white p-3 rounded-xl border border-stone-200 text-center shadow-xs">
                  <p className="font-mono font-bold text-indigo-700 text-sm">PREMIUM10</p>
                  <p className="text-[11px] text-stone-500 font-medium">10% solo en Premium</p>
                </div>
                <div className="bg-white p-3 rounded-xl border border-stone-200 text-center shadow-xs">
                  <p className="font-mono font-bold text-emerald-700 text-sm">ANUAL15</p>
                  <p className="text-[11px] text-stone-500 font-medium">15% solo en planes anuales</p>
                </div>
              </div>
              <p className="text-[11px] text-stone-500 mt-4 text-center font-medium">
                💡 Ingresa tu código en el campo de cupón durante el checkout en Stripe
              </p>
            </Card>
          </div>
        )}

        {/* FAQ Section */}
        <div className="mt-20 max-w-3xl mx-auto">
          <h3 className="text-2xl font-black text-center text-stone-900 mb-8 tracking-tight">Preguntas Frecuentes</h3>
          <div className="space-y-4">
            <Card className="p-6 bg-white border border-stone-200 rounded-2xl shadow-xs">
              <h4 className="font-bold text-stone-900 mb-1 text-sm sm:text-base">¿Cómo funciona la prueba gratuita de 14 días?</h4>
              <p className="text-stone-500 text-xs sm:text-sm leading-relaxed font-medium">
                Puedes probar todas las funciones Premium o Pro sin costo durante 14 días. Si cancelas antes de que termine el período, no se realiza ningún cargo.
              </p>
            </Card>
            <Card className="p-6 bg-white border border-stone-200 rounded-2xl shadow-xs">
              <h4 className="font-bold text-stone-900 mb-1 text-sm sm:text-base">¿Puedo cambiar de plan o cancelar después?</h4>
              <p className="text-stone-500 text-xs sm:text-sm leading-relaxed font-medium">
                Sí. Puedes subir de nivel, cambiar de ciclo de facturación o cancelar en un solo clic desde tu perfil cuando quieras sin penalización.
              </p>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Pricing;
